import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import type { ApiClient, Abgleichstand, Datenblatt, DatenblattStand, Zuweisungszahlen } from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, Feld, KpiCard, KpiReihe, LoadingState, StatusBadge,
  formatiereZahl, vollstaendigkeit, type Spalte,
} from '../../muster';
import {
  ABGLEICH_HANDELN, ABGLEICH_ZUSTAND, DATENBLATT_ZUSTAND, datenblattZustand, vollstaendigZugewiesen, warteschlangeText,
} from './datenregeln';
import { kb } from './bestandregeln';
import { Verweis } from './LueftungDatenblatt';
import './lueftung.css';

type OffenerTyp = DatenblattStand['ohneDatensatz'][number];
type Abgleichzeile = Abgleichstand['jeZustand'][number];
type Abgleichtyp = Abgleichstand['jeTyp'][number];

const KOPFTEXT = 'Das Datenblatt gehört zum Typ, nicht zum Bauteil: 6622 Bauteile sind einige hundert Kombinationen aus '
  + 'Familie und Typ. Die Liste ist nach Typ gegliedert und nach der Zahl der Bauteile sortiert - die größte Lücke steht oben.';

/**
 * Übersicht der Herstellerunterlagen je Bauteiltyp.
 *
 * Das Datenblatt gehört zum Typ, nicht zum Bauteil: 826 Brandschutzklappen
 * teilen sich eine Handvoll Größen. Deshalb ist diese Liste nach Typ gegliedert
 * und nach der Zahl der Bauteile sortiert - die größte Lücke steht oben.
 *
 * TV6d (TX-STANDARD 1.6): nach `seiten/lueftung/` umgezogen (Route bleibt
 * `lueftung/datenblaetter`). Drei `DataTable` statt vier roher Tabellen, keine
 * Kappung mehr (vorher 200 Typen ohne Datensatz), Zustand als `StatusBadge`.
 * Die Ablage je Zeile ruft wie vorher `datenblattDatei` bzw. erst
 * `datenblattFinden` (`Lueftungsdaten.schreibwege.test.tsx`).
 */
export function Datenblaetter() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const { id } = useParams<{ id: string }>();

  const [daten, setDaten] = useState<DatenblattStand | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [nurZustand, setNurZustand] = useState('');

  const laden = useCallback(async () => {
    try {
      setDaten(await api.datenblaetter(projekt.id));
      setFehler(null);
    } catch (e) {
      setFehler(e);
    }
  }, [api, projekt.id]);

  useEffect(() => { void laden(); }, [laden]);

  // Solange etwas in der Warteschlange steht, von selbst nachladen. Ohne das müsste man bei 357 Einträgen alle halbe
  // Minute die Seite neu holen.
  const inArbeit = (daten?.stand ?? [])
    .filter((s) => s.zustand === 'wartet' || s.zustand === 'laeuft')
    .reduce((n, s) => n + s.anzahl, 0);

  useEffect(() => {
    if (inArbeit === 0) return undefined;
    const takt = setInterval(() => { void laden(); }, 20_000);
    return () => clearInterval(takt);
  }, [inArbeit, laden]);

  const spalten: Spalte<Datenblatt>[] = useMemo(() => [
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', wert: (b) => DATENBLATT_ZUSTAND[b.zustand] ?? b.zustand,
      inhalt: (b) => <Zustandsmarken blatt={b} />,
    },
    { schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl', nachkomma: 0, wert: (b) => b.bauteile },
    { schluessel: 'familie', titel: 'Familie', wert: (b) => b.familie },
    { schluessel: 'typ', titel: 'Typ', immerSichtbar: true, wert: (b) => b.typ },
    {
      schluessel: 'hersteller', titel: 'Hersteller', wert: (b) => b.hersteller,
      inhalt: (b) => (b.herstellerUrl
        ? <a href={b.herstellerUrl} target="_blank" rel="noreferrer">{b.hersteller ?? 'Seite'}</a>
        : b.hersteller),
    },
    {
      schluessel: 'datenblatt', titel: 'Datenblatt', sortierbar: false, breite: '330px',
      inhalt: (b) => <Blattablage blatt={b} api={api} neuLaden={() => void laden()} />,
    },
  ], [api, laden]);

  const offeneSpalten: Spalte<OffenerTyp>[] = useMemo(() => [
    { schluessel: 'bauteile', titel: 'Bauteile', art: 'zahl', nachkomma: 0, wert: (t) => t.bauteile },
    {
      // Vor TV6d stand die Marke in einer zusätzlichen Zelle und verschob die Zeile; jetzt hat sie eine Spalte.
      schluessel: 'zugewiesen', titel: 'Werte', art: 'status', wert: (t) => t.zugewiesen,
      inhalt: (t) => (t.zugewiesen > 0
        ? (
          <StatusBadge
            klein
            zustand={vollstaendigZugewiesen(t) ? 'gut' : 'neutral'}
            text={`${formatiereZahl(t.zugewiesen, 0)}/${formatiereZahl(t.bauteile, 0)} zugewiesen`}
            titel="Bauteile dieses Typs tragen schon Werte - der Abgleich je Luftmenge legt das Datenblatt selbst an"
          />
        ) : null),
    },
    { schluessel: 'familie', titel: 'Familie', wert: (t) => t.familie },
    { schluessel: 'typ', titel: 'Typ', immerSichtbar: true, wert: (t) => t.typ },
    {
      schluessel: 'datenblatt', titel: 'Datenblatt', sortierbar: false, breite: '240px',
      inhalt: (t) => <TypAblage typ={t} api={api} projektId={projekt.id} neuLaden={() => void laden()} />,
    },
  ], [api, laden, projekt.id]);

  if (fehler !== null && !daten) {
    return (
      <>
        <PageHeader titel="Herstellerunterlagen" beschreibung={KOPFTEXT} />
        <ErrorState meldung="Die Herstellerunterlagen konnten nicht geladen werden." technisch={fehler} beiErneutVersuchen={() => void laden()} />
      </>
    );
  }
  if (!daten) {
    return (
      <>
        <PageHeader titel="Herstellerunterlagen" beschreibung={KOPFTEXT} />
        <LoadingState text="Herstellerunterlagen werden geladen …" zeilen={10} spalten={4} />
      </>
    );
  }

  const erfasst = daten.blaetter.length;
  const gepflegt = daten.blaetter.filter((b) => b.zustand === 'geprueft').length;
  const erfassung = vollstaendigkeit(erfasst, daten.typenGesamt, 100);
  const gefiltert = nurZustand ? daten.blaetter.filter((b) => b.zustand === nurZustand) : daten.blaetter;

  return (
    <>
      <PageHeader
        titel="Herstellerunterlagen"
        beschreibung={KOPFTEXT}
        meta={(
          <>
            {`${formatiereZahl(erfasst, 0)} von ${formatiereZahl(daten.typenGesamt, 0)} Bauteiltypen erfasst · `}
            <Link to={`/projekte/${id}/lueftung`}>← Lüftungsbestand</Link>
          </>
        )}
      />

      <KpiReihe>
        <KpiCard titel="Bauteiltypen im Projekt" wert={daten.typenGesamt} />
        {/* Bewertet: Jeder Typ braucht einen Datensatz (Grenze 100 %). */}
        <KpiCard titel="davon erfasst" wert={erfasst} vergleich={erfassung.text}
          zustand={daten.typenGesamt > 0 ? erfassung.zustand : undefined}
          zustandText={daten.typenGesamt > 0 ? (erfasst < daten.typenGesamt ? 'Lücke' : 'vollständig') : undefined} />
        <KpiCard titel="geprüft" wert={gepflegt} vergleich={`von ${formatiereZahl(erfasst, 0)} erfassten`} />
        <KpiCard titel="in Arbeit" wert={inArbeit} vergleich={inArbeit > 0 ? 'Abgleich läuft' : undefined} />
      </KpiReihe>

      {inArbeit > 0 && (
        <p className="lue-leise" role="status">
          <StatusBadge klein zustand="info" text="läuft" /> {warteschlangeText(inArbeit)}
        </p>
      )}

      <ContentSection titel="Bestand ergänzen">
        <Stapel api={api} projektId={projekt.id} offen={daten.ohneDatensatz.length} neuLaden={() => void laden()} />
        <Abgleichsstand api={api} projektId={projekt.id} />
        {/* Der Weg über Herstellerdaten ist der genauere: Bedingungen und Vergleichsoperatoren bleiben erhalten, und
            nichts wird gedeutet. */}
        <p className="lue-leise">
          <Link className="m-knopf" to={`/projekte/${id}/lueftung/importe`}>Herstellerdaten als JSON einlesen</Link>{' '}
          Der genauere Weg für Bauteile: Bedingungen wie Spaltbreite oder Baugröße bleiben erhalten, ebenso Angaben wie
          „&lt; 15 dB(A)“.
        </p>
      </ContentSection>

      <DataTable
        name="Bauteiltypen mit Datensatz"
        spalten={spalten}
        zeilen={gefiltert}
        schluessel={(b) => b.id}
        sortierung={{ spalte: 'bauteile', richtung: 'ab' }}
        suchePlatzhalter="Familie, Typ oder Hersteller"
        trefferText={`${formatiereZahl(gefiltert.length, 0)} von ${formatiereZahl(erfasst, 0)} Typen`}
        zeilenKlasse={(b) => (vollstaendigZugewiesen(b) ? 'zeile-zugewiesen' : undefined)}
        ausfuhr
        ausfuhrDateiname="herstellerunterlagen"
        werkzeuge={(
          <Feld
            art="auswahl"
            beschriftung="Zustand"
            wert={nurZustand}
            optionen={[
              { wert: '', beschriftung: `alle ${formatiereZahl(erfasst, 0)}` },
              ...daten.stand.map((s) => ({
                wert: s.zustand, beschriftung: `${DATENBLATT_ZUSTAND[s.zustand] ?? s.zustand} ${formatiereZahl(s.anzahl, 0)}`,
              })),
            ]}
            beiAenderung={(w) => setNurZustand(w as string)}
          />
        )}
        leer={<EmptyState klein titel={nurZustand ? 'Kein Typ in diesem Zustand.' : 'Noch kein Typ erfasst.'} />}
      />

      {/* Typen ohne Datensatz sind die eigentliche Arbeitsliste: Zu ihnen ist noch nichts unternommen worden. */}
      {daten.ohneDatensatz.length > 0 && (
        <DataTable
          name="Noch nicht erfasst"
          beschreibung={'Zu diesen Typen gibt es keinen Datensatz. „Typen anlegen“ oben erzeugt sie in einem Zug - danach '
            + 'lässt sich je Typ ein Datenblatt hochladen. Hier abgelegt, entsteht der Datensatz dabei.'}
          spalten={offeneSpalten}
          zeilen={daten.ohneDatensatz}
          schluessel={(t) => `${t.familie}\u0000${t.typ}`}
          suchePlatzhalter="Familie oder Typ"
          zeilenKlasse={(t) => (vollstaendigZugewiesen(t) ? 'zeile-zugewiesen' : undefined)}
        />
      )}
    </>
  );
}

/** Zustand und was von den Bauteilen versorgt ist - der Zustand des Datenblatts sagt das nicht. */
function Zustandsmarken({ blatt: b }: { blatt: Datenblatt }) {
  return (
    <span className="lue-marken">
      <StatusBadge klein zustand={datenblattZustand(b.zustand)} text={DATENBLATT_ZUSTAND[b.zustand] ?? b.zustand}
        titel={b.versuche > 1 ? `${formatiereZahl(b.versuche, 0)} Versuche` : undefined} />
      {b.zugewiesen > 0 && (
        <StatusBadge klein zustand={vollstaendigZugewiesen(b) ? 'gut' : 'neutral'}
          text={vollstaendigZugewiesen(b) ? 'zugewiesen' : `${formatiereZahl(b.zugewiesen, 0)}/${formatiereZahl(b.bauteile, 0)} zugewiesen`}
          titel={`${formatiereZahl(b.zugewiesen, 0)} von ${formatiereZahl(b.bauteile, 0)} Bauteil(en) tragen Werte aus diesem Datenblatt`} />
      )}
      {b.interpoliert > 0 && (
        <StatusBadge klein zustand="neutral" text={`${formatiereZahl(b.interpoliert, 0)} interpoliert`}
          titel="Zwischen zwei gedruckten Zeilen berechnet - eine begründete Annahme, keine Herstellerangabe" />
      )}
      {/* Der gefährlichste Fall ohne Kennzeichnung: Der Wert steht da und sieht richtig aus, gilt aber für die alte Luftmenge. */}
      {b.mengeGeaendert > 0 && (
        <StatusBadge klein zustand="warnung" text={`${formatiereZahl(b.mengeGeaendert, 0)} Menge geändert`}
          titel="Die Luftmenge hat sich seit dem Abgleich geändert - die Werte gelten für die alte Menge" />
      )}
      {b.mitBefund > 0 && (
        <StatusBadge klein zustand="warnung" text={`${formatiereZahl(b.mitBefund, 0)} Befund`}
          titel="Die Luftmenge liegt außerhalb des Bereichs der Herstellertabelle" />
      )}
    </span>
  );
}

/** Eine Zeilenablage: PDF ziehen oder wählen. Nimmt nur PDF an. */
function Zeilenablage({ beschriftung, titel, laeuft, ablegen, fehlerMelden }: {
  beschriftung: string;
  titel: string;
  laeuft: boolean;
  ablegen: (datei: File) => void;
  fehlerMelden: (text: string) => void;
}) {
  const [ueberZiel, setUeberZiel] = useState(false);
  const pruefen = (datei: File | undefined) => {
    if (!datei) return;
    if (!datei.name.toLowerCase().endsWith('.pdf')) { fehlerMelden('Nur PDF-Dateien.'); return; }
    ablegen(datei);
  };
  return (
    <label
      className={`m-knopf lue-dateiknopf ${ueberZiel ? 'bereit' : ''}`}
      title={titel}
      onDragOver={(e) => { e.preventDefault(); setUeberZiel(true); }}
      onDragLeave={() => setUeberZiel(false)}
      onDrop={(e) => { e.preventDefault(); setUeberZiel(false); pruefen(e.dataTransfer.files?.[0]); }}
    >
      {laeuft ? 'wird abgelegt …' : beschriftung}
      <input
        type="file"
        accept=".pdf"
        className="lue-verborgen"
        disabled={laeuft}
        onChange={(e) => { const d = e.target.files?.[0]; e.target.value = ''; pruefen(d); }}
      />
    </label>
  );
}

/**
 * Hochladen je Typ, von hier aus und nicht nur aus der Bauteilansicht: In dieser Liste steht die Reihenfolge, in der
 * man es tun will. Danach steht der gleichbleibende Verweis da, den auch die Bauteile tragen.
 */
function Blattablage({ blatt, api, neuLaden }: { blatt: Datenblatt; api: ApiClient; neuLaden: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [verweis, setVerweis] = useState<string | null>(null);

  const hochladen = async (datei: File) => {
    setLaeuft(true);
    setFehler(null);
    try {
      const a = await api.datenblattDatei(blatt.id, datei);
      setVerweis(a.verweis);
      if (!a.verweis) {
        setFehler('Hochgeladen, aber der Verweis liess sich nicht bilden - Aps__CallbackBaseUrl oder Aps__CallbackSigningKey fehlt.');
      }
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <span className="lue-zelle">
      <Zeilenablage
        beschriftung={blatt.hatDatei ? `PDF ${kb(blatt.dateiGroesse)} - ersetzen` : 'PDF ablegen'}
        titel={blatt.hatDatei ? `${blatt.dateiname} - andere Datei hierher ziehen zum Ersetzen` : 'PDF hierher ziehen oder klicken'}
        laeuft={laeuft}
        ablegen={(d) => void hochladen(d)}
        fehlerMelden={setFehler}
      />
      {/* Kein Auswerten und kein Suchen mehr: Die technischen Werte kommen über die JSON-Zuordnung. Diese Seite stellt
          nur noch das Dokument bereit - deshalb steht hier der Verweis statt eines Knopfes. */}
      {verweis && <Verweis verweis={verweis} fehlerMelden={setFehler} />}
      {!verweis && blatt.hatDatei && <span className="lue-leise">Verweis wird beim nächsten Hochladen gebildet</span>}
      {blatt.zustandHinweis && <span className="lue-leise lue-kurz" title={blatt.zustandHinweis}>{blatt.zustandHinweis}</span>}
      {fehler && <StatusBadge zustand="warnung" text={fehler} klein />}
    </span>
  );
}

/**
 * Ein Typ ohne Datensatz. Auch hier lässt sich ein Datenblatt ablegen: Der Datensatz wird dabei angelegt (mit der
 * Projektkennung - ohne sie legte der Endpunkt ein projektloses Datenblatt an, und die Bauteilansicht fände es nicht).
 */
function TypAblage({ typ, api, projektId, neuLaden }: {
  typ: Zuweisungszahlen & { familie: string; typ: string; bauteile: number };
  api: ApiClient;
  projektId: string;
  neuLaden: () => void;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const hochladen = async (datei: File) => {
    setLaeuft(true);
    setFehler(null);
    try {
      // Erst den Datensatz anlegen, dann die Datei - in einem Zug für den Anwender, zwei Aufrufe darunter.
      const blatt = await api.datenblattFinden(typ.familie, typ.typ, projektId);
      await api.datenblattDatei(blatt.id, datei);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <span className="lue-zelle">
      <Zeilenablage
        beschriftung="PDF ablegen"
        titel="PDF hierher ziehen oder klicken - der Datensatz wird dabei angelegt"
        laeuft={laeuft}
        ablegen={(d) => void hochladen(d)}
        fehlerMelden={setFehler}
      />
      {fehler && <StatusBadge zustand="warnung" text={fehler} klein />}
    </span>
  );
}

/**
 * Sammelauftrag für alle Typen. „Typen anlegen“ erzeugt nur die Datensätze und beauftragt die, zu denen ein Datenblatt
 * vorliegt; die Netzsuche wird je Bauteil einzeln ausgelöst - ein Stapel von dreihundert wäre das Gegenteil davon.
 */
function Stapel({ api, projektId, offen, neuLaden }: { api: ApiClient; projektId: string; offen: number; neuLaden: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const ausfuehren = async () => {
    setLaeuft(true);
    setFehler(null);
    setMeldung(null);
    try {
      const a = await api.datenblattSammelauftrag(projektId, 'pdf', false);
      setMeldung(`${a.typen} Typ(en) im Projekt, ${a.angelegt} neu angelegt, ${a.beauftragt} beauftragt, `
        + `${a.uebersprungen} übersprungen.` + (a.beauftragt > 0 ? ` Etwa ${a.dauerMinuten} Minuten.` : ''));
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      <div className="lue-speichern">
        <button type="button" className="m-knopf m-knopf-haupt" disabled={laeuft || offen === 0} onClick={() => void ausfuehren()}
          title="Erzeugt die Datensätze und beauftragt die, zu denen ein Datenblatt vorliegt">
          {offen > 0 ? `${offen} Typen anlegen` : 'Alle Typen erfasst'}
        </button>
        <span className="lue-leise">Die Auswertung läuft auf diesem Server, ohne Kosten. Die Netzsuche wird je Bauteil einzeln ausgelöst.</span>
      </div>
      {fehler && <ErrorState klein titel="Die Typen konnten nicht angelegt werden." meldung={fehler} />}
      <p className="lue-meldung" role="status">{meldung ?? ''}</p>
    </>
  );
}

/**
 * Was sich seit dem letzten Datenblattabgleich geändert hat. Nach einem Neueinlesen des Modells stimmen die Werte am
 * Bauteil nicht mehr zwangsläufig; die Feststellung steckt in einer Datenbanksicht, damit die Regel an einer Stelle liegt.
 */
function Abgleichsstand({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<Abgleichstand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      setStand(await api.abgleichstand(projektId));
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, projektId]);

  useEffect(() => { void laden(); }, [laden]);

  const aufraeumen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const a = await api.abgleichAufraeumen(projektId);
      setMeldung(a.message);
      void laden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const zustandSpalten: Spalte<Abgleichzeile>[] = useMemo(() => [
    { schluessel: 'anzahl', titel: 'Bauteile', art: 'zahl', nachkomma: 0, breite: '110px', wert: (z) => z.anzahl },
    {
      schluessel: 'zustand', titel: 'Stand', immerSichtbar: true, wert: (z) => ABGLEICH_ZUSTAND[z.zustand] ?? z.zustand,
      inhalt: (z) => (ABGLEICH_HANDELN.has(z.zustand)
        ? <StatusBadge klein zustand="warnung" text={ABGLEICH_ZUSTAND[z.zustand] ?? z.zustand} />
        : <span>{ABGLEICH_ZUSTAND[z.zustand] ?? z.zustand}</span>),
    },
  ], []);

  const typSpalten: Spalte<Abgleichtyp>[] = useMemo(() => [
    { schluessel: 'anzahl', titel: 'Bauteile', art: 'zahl', nachkomma: 0, breite: '110px', wert: (t) => t.anzahl },
    { schluessel: 'typ', titel: 'Typ', immerSichtbar: true, wert: (t) => t.typ },
    { schluessel: 'familie', titel: 'Familie', wert: (t) => t.familie },
    { schluessel: 'zustand', titel: 'Stand', wert: (t) => ABGLEICH_ZUSTAND[t.zustand] ?? t.zustand },
  ], []);

  if (fehler && !stand) return <ErrorState klein titel="Der Stand des Abgleichs konnte nicht geladen werden." meldung={fehler} />;
  if (!stand) return null;

  const entfernt = stand.jeZustand.find((z) => z.zustand === 'entfernt')?.anzahl ?? 0;

  return (
    <div className="lue-baureihe">
      <DataTable
        name="Stand des Abgleichs"
        beschreibung={stand.nachzufuehren > 0
          ? `${formatiereZahl(stand.nachzufuehren, 0)} nachzuführen - über „Herstellerdaten einlesen“, dort je Typ „Je Bauteil abgleichen“ erneut ausführen.`
          : 'Nachgeführt wird über „Herstellerdaten einlesen“ - dort je Typ „Je Bauteil abgleichen“ erneut ausführen.'}
        spalten={zustandSpalten}
        zeilen={stand.jeZustand}
        schluessel={(z) => z.zustand}
        suche={false}
        spaltenwahl={false}
        dicht
      />

      {stand.jeTyp.length > 0 && (
        <details className="lue-aufklapp">
          <summary>Welche Typen betroffen sind ({formatiereZahl(stand.jeTyp.length, 0)})</summary>
          <DataTable
            name="Betroffene Typen"
            spalten={typSpalten}
            zeilen={stand.jeTyp}
            schluessel={(t) => `${t.zustand}|${t.familie}|${t.typ}`}
            dicht
          />
        </details>
      )}

      {entfernt > 0 && (
        <div className="lue-speichern">
          <button type="button" className="m-knopf" disabled={laeuft} onClick={() => void aufraeumen()}>
            {entfernt} entfernte(s) Bauteil(e) vom Datenblatt lösen
          </button>
          <span className="lue-leise">Die Werte bleiben erhalten - ein Bauteil kann zeitweise aus dem Modell verschwinden.</span>
        </div>
      )}

      {fehler && stand && <ErrorState klein titel="Die Bauteile konnten nicht gelöst werden." meldung={fehler} />}
      <p className="lue-meldung" role="status">{meldung ?? ''}</p>
    </div>
  );
}
